import React, { useState } from 'react';
import axios from '../utils/axios';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';

interface SubscriptionPaymentFormProps {
  planName: string;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  creditsPerMonth: number;
  onSuccess: (data: any) => void;
  onCancel: () => void;
}

export const SubscriptionPaymentForm: React.FC<SubscriptionPaymentFormProps> = ({
  planName,
  billingCycle,
  amount,
  creditsPerMonth,
  onSuccess,
  onCancel
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    cardholderName: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError('');

    try {
      const response = await axios.post('/api/mx-merchant/subscribe', {
        plan_name: planName,
        billing_cycle: billingCycle,
        card_number: formData.cardNumber,
        exp_month: formData.expMonth,
        exp_year: formData.expYear,
        cvv: formData.cvv,
        cardholder_name: formData.cardholderName
      });

      onSuccess(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Subscription failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
      {/* Subscription Summary */}
      <div style={{ 
        background: 'var(--color-gray-50)', 
        padding: 'var(--spacing-4)', 
        borderRadius: 'var(--radius-lg)', 
        marginBottom: 'var(--spacing-6)' 
      }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
          {planName} Plan
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
          <span style={{ color: 'var(--color-gray-600)' }}>Billing Cycle:</span>
          <span style={{ fontWeight: 'var(--font-semibold)' }}>{billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
          <span style={{ color: 'var(--color-gray-600)' }}>Credits:</span>
          <span style={{ fontWeight: 'var(--font-semibold)' }}>{creditsPerMonth} per month</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--spacing-2)', borderTop: '1px solid var(--color-gray-200)' }}>
          <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)' }}>Total:</span>
          <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary)' }}>
            ${amount}{billingCycle === 'yearly' ? '/year' : '/month'}
          </span>
        </div>
      </div>

      {/* Payment Form */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
          Cardholder Name
        </label>
        <input
          type="text"
          name="cardholderName"
          value={formData.cardholderName}
          onChange={handleInputChange}
          required
          placeholder="John Doe"
          style={{
            width: '100%',
            padding: 'var(--spacing-3)',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-base)'
          }}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
          Card Number
        </label>
        <input
          type="text"
          name="cardNumber"
          value={formatCardNumber(formData.cardNumber)}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/\s/g, '');
            if (cleaned.length <= 16) {
              handleInputChange({ ...e, target: { ...e.target, value: cleaned } });
            }
          }}
          required
          placeholder="1234 5678 9012 3456"
          maxLength={19}
          style={{
            width: '100%',
            padding: 'var(--spacing-3)',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-base)'
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            Exp Month
          </label>
          <input
            type="text"
            name="expMonth"
            value={formData.expMonth}
            onChange={handleInputChange}
            required
            placeholder="MM"
            maxLength={2}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-base)'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            Exp Year
          </label>
          <input
            type="text"
            name="expYear"
            value={formData.expYear}
            onChange={handleInputChange}
            required
            placeholder="YY"
            maxLength={2}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-base)'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            CVV
          </label>
          <input
            type="text"
            name="cvv"
            value={formData.cvv}
            onChange={handleInputChange}
            required
            placeholder="123"
            maxLength={4}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-base)'
            }}
          />
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {/* Security Notice */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--spacing-2)', 
        padding: 'var(--spacing-3)', 
        background: 'var(--color-gray-50)', 
        borderRadius: 'var(--radius-md)', 
        marginBottom: 'var(--spacing-6)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-gray-600)'
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <span>Your payment information is encrypted and secure</span>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
        <Button type="button" variant="outline" onClick={onCancel} fullWidth disabled={isProcessing}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={isProcessing} fullWidth>
          Subscribe Now
        </Button>
      </div>

      <p style={{ 
        marginTop: 'var(--spacing-4)', 
        fontSize: 'var(--text-xs)', 
        color: 'var(--color-gray-500)', 
        textAlign: 'center' 
      }}>
        You can cancel your subscription at any time from your account settings.
      </p>
    </form>
  );
};
